import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DataTable,
  Pagination,
  StatusBadge,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { HiArrowLeft } from 'react-icons/hi2';
import { useReunificationEvents } from '../../hooks/queries/useReunification';
import { useComplianceReport } from '../../hooks/queries/useDocuments';
import type {
  ReunificationStatus,
} from '../../types/ReunificationTypes';

interface ComplianceReportsProps {
  onBack: () => void;
}

const statusMap: Record<string, StatusType> = {
  excellent: 'success',
  good: 'success',
  acceptable: 'info',
  needs_improvement: 'warning',
  critical: 'error',
};

function ratingLabel(rating: string): string {
  return rating.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Compliance Detail ────────────────────────────────────────────────────────

function ComplianceDetail({
  eventId,
  onBack,
}: {
  eventId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useComplianceReport(eventId);

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <VStack gap={3}>
          <Text color="text.danger">
            {error instanceof Error
              ? error.message
              : t('documents.failedLoadComplianceReport', 'Failed to load compliance report')}
          </Text>
          <ButtonComponent visual="secondary" size="sm" onClick={onBack} data-e2e="back-to-events-button">
            {t('documents.backToEvents', 'Back to Events')}
          </ButtonComponent>
        </VStack>
      </Center>
    );
  }

  if (!data) return null;

  return (
    <VStack gap={6} align="stretch">
      <Flex align="center" gap={3}>
        <ButtonComponent visual="secondary" size="sm" onClick={onBack} data-e2e="back-button">
          <HiArrowLeft />
          {t('common.back', 'Back')}
        </ButtonComponent>
        <Box>
          <Heading as="h3" fontSize="md" fontWeight={600} color="text.primary">
            {t('documents.complianceReport', 'Compliance Report')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {data.compliance_standard} &mdash;{' '}
            {new Date(data.generated_at).toLocaleDateString()}
          </Text>
        </Box>
      </Flex>

      {/* Score summary */}
      <SimpleGrid columns={{ base: 2, md: 3 }} gap={4}>
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.primary"
          borderRadius="lg"
          p={4}
          textAlign="center"
        >
          <Text fontSize="3xl" fontWeight={700} color="text.primary">
            {data.compliance_score}%
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {t('documents.complianceScore', 'Compliance Score')}
          </Text>
        </Box>
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.primary"
          borderRadius="lg"
          p={4}
          textAlign="center"
        >
          <StatusBadge
            label={ratingLabel(data.rating)}
            status={statusMap[data.rating] ?? 'info'}
          />
          <Text fontSize="sm" color="text.secondary" mt={2}>
            {t('documents.rating', 'Rating')}
          </Text>
        </Box>
        <Box
          bg="surface.primary"
          border="1px solid"
          borderColor="border.primary"
          borderRadius="lg"
          p={4}
          textAlign="center"
        >
          <Text fontSize="3xl" fontWeight={700} color="text.primary">
            {data.compliant_items.length}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {t('documents.requirementsMetCount', 'of {{total}} requirements met', {
              total: data.compliant_items.length + data.non_compliant_items.length,
            })}
          </Text>
        </Box>
      </SimpleGrid>

      {/* Non-compliant items */}
      {data.non_compliant_items.length > 0 && (
        <Box>
          <Text fontWeight={600} color="text.primary" mb={3}>
            {t('documents.needsAttention', 'Needs Attention')}
          </Text>
          <VStack gap={2} align="stretch">
            {data.non_compliant_items.map((item, i) => (
              <Box
                key={i}
                bg="surface.primary"
                border="1px solid"
                borderColor="border.primary"
                borderRadius="md"
                p={3}
              >
                <Flex justify="space-between" align="center" mb={1}>
                  <Text fontWeight={500} color="text.primary">
                    {item.requirement}
                  </Text>
                  <StatusBadge
                    label={item.status === 'partial' ? t('documents.partial', 'Partial') : t('documents.notMet', 'Not Met')}
                    status={item.status === 'partial' ? 'warning' : 'error'}
                  />
                </Flex>
                <Text fontSize="sm" color="text.secondary">
                  {item.details}
                </Text>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* Compliant items */}
      {data.compliant_items.length > 0 && (
        <Box>
          <Text fontWeight={600} color="text.primary" mb={3}>
            {t('documents.compliant', 'Compliant')}
          </Text>
          <VStack gap={2} align="stretch">
            {data.compliant_items.map((item, i) => (
              <Box
                key={i}
                bg="surface.primary"
                border="1px solid"
                borderColor="border.primary"
                borderRadius="md"
                p={3}
              >
                <Flex justify="space-between" align="center">
                  <Text fontWeight={500} color="text.primary">
                    {item.requirement}
                  </Text>
                  <StatusBadge label={t('documents.met', 'Met')} status="success" />
                </Flex>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <Box>
          <Text fontWeight={600} color="text.primary" mb={3}>
            {t('documents.recommendations', 'Recommendations')}
          </Text>
          <VStack gap={1} align="stretch">
            {data.recommendations.map((rec, i) => (
              <Text key={i} fontSize="sm" color="text.secondary">
                &bull; {rec}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

interface EventRow {
  id: string;
  school: string;
  plan: string;
  incident_type: string;
  status: string;
  completed_at: string;
}

const statusColorMap: Record<ReunificationStatus, string> = {
  pending: 'yellow',
  active: 'blue',
  paused: 'orange',
  completed: 'green',
  cancelled: 'red',
};

export function ComplianceReports({ onBack }: ComplianceReportsProps) {
  const { t } = useTranslation();
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const columns: Column<EventRow>[] = useMemo(
    () => [
      { key: 'school', label: t('nav.sites', 'School'), sortable: true },
      { key: 'plan', label: t('reunification.plan', 'Plan'), sortable: true },
      { key: 'incident_type', label: t('events.incident', 'Incident'), sortable: true },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        render: (row) => (
          <StatusBadge
            label={row.status}
            status={
              (statusColorMap[row.status as ReunificationStatus] as StatusType) ??
              'info'
            }
          />
        ),
      },
      {
        key: 'completed_at',
        label: t('events.completed', 'Completed'),
        sortable: true,
        render: (row) =>
          row.completed_at
            ? new Date(row.completed_at).toLocaleDateString()
            : '—',
      },
    ],
    [t],
  );

  const { data, isLoading, error } = useReunificationEvents({
    status: 'completed',
    page,
    page_size: pageSize,
    ordering: '-completed_at',
  });

  if (selectedEvent) {
    return (
      <Box px={{ base: 4, md: 8 }} py={6}>
        <ComplianceDetail
          eventId={selectedEvent}
          onBack={() => setSelectedEvent(null)}
        />
      </Box>
    );
  }

  const rows: EventRow[] =
    data?.results?.map((e) => ({
      id: e.public_id,
      school: e.school_name,
      plan: e.plan_name ?? '—',
      incident_type: e.incident_type_display,
      status: e.status_display,
      completed_at: e.completed_at ?? '',
    })) ?? [];

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex align="center" gap={3} mb={6}>
        <ButtonComponent visual="secondary" size="sm" onClick={onBack} data-e2e="back-button">
          <HiArrowLeft />
          {t('common.back', 'Back')}
        </ButtonComponent>
        <Box>
          <Heading as="h2" fontSize="lg" fontWeight={600} color="text.primary">
            {t('documents.complianceReports', 'Compliance Reports')}
          </Heading>
          <Text fontSize="sm" color="text.secondary">
            {t('documents.selectCompletedEventAssessmentInstruction', 'Select a completed event to view its ILUG SRM compliance assessment.')}
          </Text>
        </Box>
      </Flex>

      {isLoading && (
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      )}

      {error && (
        <Center py={12}>
          <Text color="text.danger">
            {error instanceof Error
              ? error.message
              : t('documents.failedLoadEvents', 'Failed to load events')}
          </Text>
        </Center>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <Center py={12}>
          <Text color="text.secondary">
            {t('documents.noCompletedEventsFound', 'No completed reunification events found.')}
          </Text>
        </Center>
      )}

      {rows.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={rows}
            onRowClick={(row) => setSelectedEvent(row.id)}
            keyExtractor={(row) => row.id}
          />
          {data && data.count > pageSize && (
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(data.count / pageSize)}
              onPageChange={setPage}
              pageSize={pageSize}
            />
          )}
        </>
      )}
    </Box>
  );
}
